import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  ArrowLeftIcon,
  BanknoteIcon,
  BeakerIcon,
  CalculatorIcon,
  ClipboardListIcon,
  InfoIcon,
  PackageIcon,
} from "lucide-react";
import { useEffect, useMemo } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { BranchRow } from "@/modules/branches/domain/types";
import { branchesApi } from "@/modules/branches/presentation/api/client";
import { catalogProductsApi } from "@/modules/catalog/presentation/api/client";
import {
  costPerBaseLak,
  toBaseQty,
} from "@/modules/inventory/domain/lib/unit-conversion";
import type {
  CreateLotPayload,
  IngredientListRow,
} from "@/modules/inventory/domain/types";
import {
  FormInput,
  FormNumber,
  FormPrice,
  FormRoot,
  FormSelect,
  RHF,
  zodResolver,
} from "@/shared/forms";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import { inventoryIngredientsApi } from "../api/client";
import { useCreateLot, useIngredientQuery } from "../api/queries";

type RetailVariantOption = {
  id: string;
  name: string;
  productName: string;
};

type GrnItemOption = IngredientListRow | RetailVariantOption;

function isRetailVariant(item: GrnItemOption): item is RetailVariantOption {
  return "productName" in item;
}

const GrnFormSchema = z.object({
  branchId: z.string().min(1, "ຕ້ອງເລືອກສາຂາ"),
  itemType: z.enum(["INGREDIENT", "RETAIL_VARIANT"]),
  itemId: z.string().min(1, "ຕ້ອງເລືອກວັດຖຸດິບຫຼືຕົວແບບ"),
  lotNo: z.string().min(1, "ຕ້ອງໃສ່ເລກ lot"),
  expiryDate: z.string().optional(),
  receivedAt: z.string().min(1, "ຕ້ອງໃສ່ວັນທີຮັບ"),
  qtyInBaseUnit: z.coerce.number().positive("ປະລິມານຕ້ອງໃຫຍ່ກວ່າ 0"),
  costPerUnitLak: z
    .union([z.string(), z.number()])
    .optional()
    .transform((v) => (v === "" || v === undefined ? undefined : Number(v))),
});

type GrnFormValues = z.output<typeof GrnFormSchema>;

function toDatetimeLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const h = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${y}-${m}-${day}T${h}:${min}`;
}

function generateLotNo(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  const suffix = Math.random().toString(36).slice(2, 6).toUpperCase();
  return `LOT-${y}${m}${d}-${suffix}`;
}

export function GrnFormPage() {
  const nav = useNavigate({ from: "/app/inventory/lots/receive" });
  const createLot = useCreateLot();
  const { data: branchesData } = useQuery({
    queryKey: ["branches", "list", { limit: 200, offset: 0 }],
    queryFn: () => branchesApi.list({ limit: 200, offset: 0 }),
  });

  const branches: BranchRow[] = branchesData?.data ?? [];
  const branchOptions = useMemo(
    () => branches.map((b) => ({ value: b.id, label: b.name })),
    [branches],
  );

  const methods = RHF.useForm<GrnFormValues>({
    resolver: zodResolver(GrnFormSchema) as RHF.Resolver<GrnFormValues>,
    defaultValues: {
      branchId: "",
      itemType: "INGREDIENT",
      itemId: "",
      lotNo: generateLotNo(),
      expiryDate: "",
      receivedAt: toDatetimeLocal(new Date()),
      qtyInBaseUnit: 1,
      costPerUnitLak: undefined,
    },
  });

  const itemType = methods.watch("itemType");
  const itemId = methods.watch("itemId");
  const qtyInBaseUnit = methods.watch("qtyInBaseUnit");
  const costPerUnitLak = methods.watch("costPerUnitLak");

  const { data: selectedIngredient } = useIngredientQuery(
    itemType === "INGREDIENT" ? itemId : "",
  );
  const costNum = costPerUnitLak != null ? Number(costPerUnitLak) : 0;
  const qtyNum =
    typeof qtyInBaseUnit === "number"
      ? qtyInBaseUnit
      : Number(qtyInBaseUnit) || 0;
  const receiveFactor =
    itemType === "INGREDIENT" && selectedIngredient
      ? selectedIngredient.receiveToBase
      : 1;
  const displayUnit =
    itemType === "INGREDIENT" && selectedIngredient
      ? selectedIngredient.receiveUnit
      : itemType === "RETAIL_VARIANT"
        ? "pcs"
        : "–";
  const basePreview =
    itemType === "INGREDIENT" && selectedIngredient && receiveFactor !== 1
      ? `${toBaseQty(qtyNum, receiveFactor)} ${selectedIngredient.baseUnit}`
      : null;
  const roundedBaseCost =
    itemType === "INGREDIENT" &&
    selectedIngredient &&
    costNum > 0 &&
    receiveFactor > 1
      ? costPerBaseLak(costNum, receiveFactor)
      : null;
  const estimatedTotalLak = qtyNum * costNum;

  useEffect(() => {
    methods.setValue("itemId", "");
  }, [itemType, methods.setValue]);

  const itemLookupQueryFn = useMemo(
    () =>
      ({ search, pageParam }: { search: string; pageParam: number }) =>
        itemType === "INGREDIENT"
          ? inventoryIngredientsApi.lookup({
              search: search || undefined,
              page: pageParam,
              limit: 20,
            })
          : catalogProductsApi.listRetailVariantsLookup({
              search: search || undefined,
              page: pageParam,
              limit: 20,
            }),
    [itemType],
  );

  const itemPreloadQueryFn = useMemo(
    () => (id: string) =>
      itemType === "INGREDIENT"
        ? inventoryIngredientsApi.get(id).catch(() => null)
        : catalogProductsApi.getRetailVariant(id),
    [itemType],
  );

  const onSubmit = async (values: GrnFormValues) => {
    const payload: CreateLotPayload = {
      branchId: values.branchId,
      itemType: values.itemType,
      itemId: values.itemId,
      lotNo: values.lotNo.trim(),
      expiryDate: values.expiryDate?.trim() || null,
      receivedAt: new Date(values.receivedAt).toISOString(),
      qtyInBaseUnit: values.qtyInBaseUnit,
      costPerUnitLak:
        values.costPerUnitLak != null
          ? Math.round(Number(values.costPerUnitLak))
          : null,
    };
    try {
      await createLot.mutateAsync(payload);
      toast.success("ບັນທຶກການຮັບເຂົ້າ lot ສຳເລັດ");
      nav({ to: "/app/inventory/lots" });
    } catch (e: unknown) {
      const err = e as { message?: string };
      toast.error(err?.message ?? "ບັນທຶກລົ້ມເຫຼວ");
    }
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            {
              label: "ລາຍການ Lot",
              to: "/app/inventory/lots",
            },
            {
              label: "ຮັບເຂົ້າ Lot",
            },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-6 flex flex-wrap items-start justify-between gap-2">
          <div>
            <h2 className="font-bold text-2xl tracking-tight">
              ຮັບເຂົ້າ Lot (GRN)
            </h2>
            <p className="text-muted-foreground">
              ບັນທຶກການຮັບສິນຄ້າເຂົ້າສາງເປັນ lot ຕໍ່ສາຂາ
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => nav({ to: "/app/inventory/lots" })}
            className="gap-2"
          >
            <ArrowLeftIcon className="size-4" />
            ກັບຄືນ
          </Button>
        </div>

        <Card className="rounded-xl border bg-card">
          <FormRoot<GrnFormValues>
            methods={methods}
            onSubmit={onSubmit}
            className="space-y-0"
          >
            <CardContent className="space-y-6">
              {/* Section 1: General Information */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <InfoIcon className="size-5 text-muted-foreground" />
                  <h3 className="font-heading font-semibold text-base">
                    ຂໍ້ມູນທົ່ວໄປ
                  </h3>
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  <FormSelect
                    name="branchId"
                    label="ສາຂາ"
                    options={branchOptions}
                    placeholder="ເລືອກສາຂາ"
                    requiredMark
                  />
                  <div className="space-y-2">
                    <span className="font-medium text-sm">ປະເພດສິນຄ້າ</span>
                    <RHF.Controller
                      control={methods.control}
                      name="itemType"
                      render={({ field }) => (
                        <Tabs
                          value={field.value}
                          onValueChange={(v) =>
                            field.onChange(v as "INGREDIENT" | "RETAIL_VARIANT")
                          }
                          className="w-full"
                        >
                          <TabsList className="grid w-full grid-cols-2">
                            <TabsTrigger value="INGREDIENT">
                              <BeakerIcon className="size-4" />
                              ວັດຖຸດິບ
                            </TabsTrigger>
                            <TabsTrigger value="RETAIL_VARIANT">
                              <PackageIcon className="size-4" />
                              ຕົວແບບສິນຄ້າ
                            </TabsTrigger>
                          </TabsList>
                        </Tabs>
                      )}
                    />
                  </div>
                </div>
                <FormInfiniteCombobox<GrnItemOption>
                  name="itemId"
                  label="ລາຍການ"
                  queryKey={["grn", "item", itemType]}
                  queryFn={itemLookupQueryFn}
                  preloadQueryFn={itemPreloadQueryFn}
                  getLabel={(item) =>
                    isRetailVariant(item)
                      ? `${item.productName} – ${item.name}`
                      : `${item.name} (${item.receiveUnit || item.baseUnit})`
                  }
                  getValue={(item) => item.id}
                  placeholder="ຄົ້ນຫາຕາມຊື່, SKU ຫຼື barcode..."
                  requiredMark
                  clearable
                />
              </div>

              {/* Section 2: Lot Tracking Details */}
              <div className="space-y-4 border-t pt-6">
                <div className="flex items-center gap-2">
                  <PackageIcon className="size-5 text-muted-foreground" />
                  <h3 className="font-heading font-semibold text-base">
                    ລາຍລະອຽດ Lot
                  </h3>
                </div>
                <div className="grid gap-2 sm:grid-cols-3">
                  <FormInput
                    name="lotNo"
                    label="ເລກ Lot"
                    placeholder="ສ້າງອັດຕະໂນມັດ"
                    readOnly
                    requiredMark
                  />
                  <FormInput
                    name="expiryDate"
                    label="ວັນໝົດອາຍຸ"
                    type="date"
                    placeholder="ວັນ/ເດືອນ/ປີ"
                  />
                  <FormInput
                    name="receivedAt"
                    label="ວັນທີຮັບ"
                    type="datetime-local"
                    requiredMark
                  />
                </div>
              </div>

              {/* Section 3: Quantity & Financials */}
              <div className="space-y-4 border-t pt-6">
                <div className="flex items-center gap-2">
                  <BanknoteIcon className="size-5 text-muted-foreground" />
                  <h3 className="font-heading font-semibold text-base">
                    ປະລິມານ ແລະ ການເງິນ
                  </h3>
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  <div className="flex flex-wrap items-end gap-2">
                    <div className="min-w-[140px] flex-1">
                      <FormNumber
                        name="qtyInBaseUnit"
                        label="ປະລິມານຮັບ"
                        step="any"
                        min={0}
                        requiredMark
                      />
                    </div>
                    <div className="flex h-9 min-w-16 items-center rounded-md border bg-muted/50 px-3 font-medium text-muted-foreground text-sm">
                      {displayUnit}
                    </div>
                    {basePreview ? (
                      <p className="w-full text-muted-foreground text-xs">
                        = {basePreview} ໃນຄັງ
                        {roundedBaseCost === 1
                          ? " · ຕົ້ນທຶນຕໍ່ຫນ່ວຍພື້ນຖານປັດເຫຼືອ 1 ກີບ"
                          : ""}
                      </p>
                    ) : null}
                  </div>
                  <div className="flex flex-wrap items-end gap-2">
                    <FormPrice
                      name="costPerUnitLak"
                      label="ຕົ້ນທຶນຕໍ່ຫນ່ວຍ (ກີບ)"
                      requiredMark
                      currencySymbol="K"
                      placeholder="ຕົວຢ່າງ: 1,000"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between gap-2 rounded-md border bg-muted/50 p-2">
                  <div className="flex items-center gap-2">
                    <CalculatorIcon className="size-5 text-muted-foreground" />
                    <span className="font-medium text-sm">ມູນຄ່າປະເມີນລວມ:</span>
                  </div>
                  <span className="font-semibold text-lg">
                    {Number(estimatedTotalLak).toLocaleString("lo-LA")} ກີບ
                  </span>
                </div>
              </div>

              <div className="flex justify-end gap-2 border-t pt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => nav({ to: "/app/inventory/lots" })}
                >
                  ຍົກເລີກ
                </Button>
                <Button
                  type="submit"
                  disabled={createLot.isPending}
                  className="gap-2"
                >
                  <ClipboardListIcon className="size-4" />
                  {createLot.isPending ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກການຮັບ"}
                </Button>
              </div>
            </CardContent>
          </FormRoot>
        </Card>
      </Main>
    </>
  );
}
